import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { ROUTES } from '@app/router/routes';
import { useLanguage } from '@shared/contexts/LanguageContext';

// ─── 日期格式化工具 ───────────────────────────────────────────────────────────
function fmtDate(gmtStr) {
  if (!gmtStr) return '';
  const d = new Date(gmtStr);
  if (isNaN(d)) return gmtStr;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Chip 標籤 ────────────────────────────────────────────────────────────────
function Chip({ color, label }) {
  const bg = color === 'red' ? '#BC313D' : '#C59B5A';
  return (
    <div
      className="flex-shrink-0 flex items-center justify-center"
      style={{ paddingBottom: 2, paddingLeft: 8, paddingRight: 8, background: bg, borderRadius: 8, outline: `1px ${bg} solid`, outlineOffset: '-1px' }}
    >
      <span style={{ color: 'white', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '19.5px' }}>{label}</span>
    </div>
  );
}

// ─── 可收合區塊 ───────────────────────────────────────────────────────────────
function CollapsibleSection({ title, subtitle, totalPoints, children, onRuleClick }) {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage?.code === 'en';
  const [open, setOpen] = useState(true);
  return (
    <div className="flex flex-col w-full">
      <div
        className="flex items-center gap-4"
        style={{ alignSelf: 'stretch', paddingLeft: 26, borderLeft: '6px solid #BC313D' }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{ color: '#171717', fontSize: isEn ? 16 : (title.length > 3 ? 20 : 24), fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >{title}</span>
            {subtitle && <span style={{ color: '#BC313D', fontSize: isEn ? 16 : (title.length > 3 ? 20 : 24), fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '1.4', display: 'block' }}>{subtitle}</span>}
          </div>
          {totalPoints != null && <span style={{ color: '#BC313D', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '28px', letterSpacing: 0.38, verticalAlign: 'bottom', marginLeft: 16 }}>
            {totalPoints > 0 ? '+' + totalPoints : totalPoints}
          </span>}
        </div>
        <button onClick={onRuleClick} style={{ border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }} aria-label={t('profile.viewRulesAria')}>
          <span className="material-symbols-outlined text-[#6E625B]/40 text-base flex-shrink-0" style={{ fontVariationSettings: "'FILL' 0", fontSize: 20, verticalAlign: 'top' }}>info</span>
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-shrink-0"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <svg
            width="24" height="24" viewBox="0 0 24 24" fill="none"
            style={{ transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }}
          >
            <path d="M7 15L12 10L17 15" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div style={{ paddingLeft: 32 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function TimelineDivider() {
  return <div style={{ height: 16 }} />;
}

// ─── 可 scroll 列表容器 ───────────────────────────────────────────────────────
const PREVIEW_COUNT = 3;
const EXPANDED_MAX_HEIGHT = 480;

function ScrollableList({ loading, empty, items = [], renderItem }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const hasMore = items.length > PREVIEW_COUNT;
  const displayed = expanded ? items : items.slice(0, PREVIEW_COUNT);

  return (
    <div style={{ paddingTop: 24, paddingBottom: 24 }}>
      {loading ? (
        <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.loading')}</span>
      ) : items.length === 0 ? (
        <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{empty}</span>
      ) : (
        <>
          <div
            className="flex flex-col gap-2"
            style={expanded ? { maxHeight: EXPANDED_MAX_HEIGHT, overflowY: 'auto' } : {}}
          >
            {displayed.map((item, i) => renderItem(item, i))}
          </div>
          {hasMore && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center justify-center gap-1 w-full"
              style={{ marginTop: 12, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}
            >
              <span style={{ color: '#6E625B', fontSize: 14, fontFamily: 'Noto Sans TC', fontWeight: 400 }}>
                {expanded ? t('profile.collapse') : t('profile.expandAll').replace('{{count}}', items.length)}
              </span>
              <svg
                width="16" height="16" viewBox="0 0 16 16" fill="none"
                style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <path d="M4 6L8 10L12 6" stroke="#6E625B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── 禪心連線 看板卡片 ────────────────────────────────────────────────────────
function ZenBoardCard({ item, onClick }) {
  const { t } = useLanguage();
  const dateObj = new Date(item.board_start_at);
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  const dateStr = `${m}/${d}`;

  return (
    <div
      className={`w-full pr-2.5 bg-White-White_30/30 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#171717]/15 inline-flex justify-start items-center gap-3 transition-all duration-150 ${item.point > 0 ? 'cursor-pointer hover:brightness-95 active:scale-95' : ''}`}
      onClick={() => item.point > 0 && onClick && onClick(item.board_id)}
    >
      <div
        data-color="gold"
        data-state="on"
        className="px-2 pt-1 pb-[5px] rounded-sm outline outline-1 outline-offset-[-1px] outline-[#c59b5a] flex justify-center items-center gap-2.5 flex-shrink-0 bg-[#c59b5a]"
      >
        <span className="justify-start text-amber-50 font-medium font-['Noto_Sans_TC'] leading-7">
          {dateStr}
        </span>
      </div>
      <div className="flex-1 justify-center text-stone-500 text-lg font-bold font-['Noto_Sans_TC'] leading-7 truncate">
        {item.board_title}
      </div>
      <div className="flex justify-start items-center gap-1 flex-shrink-0">
        <span className="text-center justify-center font-['Noto_Sans_TC'] leading-7"
          style={(item.point === 0 ? { fontSize: 12, color: '#6E625B' } : { color: '#bc313d', fontSize: 18, fontWeight: 700 })}>
          {item.point === 0 ? t('profile.noRecord') : `+${item.point}`}
        </span>
      </div>
      {item.point > 0 ? (
        <div className="w-4 h-4 relative overflow-hidden flex-shrink-0">
          <ChevronRight size={16} color="#6E625B" />
        </div>
      ) : (<div className="w-4 h-4 relative overflow-hidden flex-shrink-0" />)}
    </div>
  );
}

// ─── 庭影覺光 投稿卡片 ────────────────────────────────────────────────────────
function ShadowCard({ item, onClick }) {
  const dateObj = new Date(item.created_at || item.tx_time);
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  const dateStr = `${m}/${d}`;

  return (
    <div
      className="w-full pr-2.5 bg-White-White_30/30 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#171717]/15 inline-flex justify-start items-center gap-3 cursor-pointer hover:brightness-95 active:scale-95 transition-all duration-150"
      onClick={() => onClick && onClick(item.topic_id)}
    >
      <div
        className="px-2 pt-1 pb-[5px] rounded-sm outline outline-1 outline-offset-[-1px] outline-[#c59b5a] flex justify-center items-center gap-2.5 flex-shrink-0 bg-[#c59b5a]"
      >
        <span className="justify-start text-amber-50 font-medium font-['Noto_Sans_TC'] leading-7">
          {dateStr}
        </span>
      </div>
      <div className="flex-1 justify-center text-stone-500 text-lg font-bold font-['Noto_Sans_TC'] leading-7 truncate">
        {item.topic_title}
      </div>
      <div className="flex justify-start items-center gap-1 flex-shrink-0">
        <span className="text-center justify-center font-['Noto_Sans_TC'] leading-7"
          style={{ color: '#bc313d', fontSize: 18, fontWeight: 700 }}>
          +{item.point}
        </span>
      </div>
      <div className="w-4 h-4 relative overflow-hidden flex-shrink-0">
        <ChevronRight size={16} color="#6E625B" />
      </div>
    </div>
  );
}

// ─── 文星點燈 心形卡片 ────────────────────────────────────────────────────────
function LightingHeartCard({ item }) {
  const hasPoint = item.point > 0;
  return (
    <div
      className="flex items-center gap-3 w-full"
      style={{
        paddingRight: 12,
        background: 'rgba(255,255,255,0.30)',
        borderRadius: 8,
        outline: '1px rgba(23,23,23,0.10) solid',
        outlineOffset: '-1px',
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{ width: 50, paddingTop: 8, paddingBottom: 8, paddingLeft: 4, paddingRight: 4, flexDirection: 'column' }}
      >
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path
            d="M15 25.5C15 25.5 3 18 3 10.5C3 7.46 5.46 5 8.5 5C10.24 5 11.79 5.88 12.9 7.18C13.28 7.63 13.62 8.11 13.9 8.62L15 10.5L16.1 8.62C16.38 8.11 16.72 7.63 17.1 7.18C18.21 5.88 19.76 5 21.5 5C24.54 5 27 7.46 27 10.5C27 18 15 25.5 15 25.5Z"
            fill="url(#heart_grad)" stroke="rgba(188,49,61,0.10)" strokeWidth="1.5"
          />
          <defs>
            <linearGradient id="heart_grad" x1="27" y1="25.5" x2="3" y2="5" gradientUnits="userSpaceOnUse">
              <stop offset="7%" stopColor="rgba(252,248,248,0.80)" />
              <stop offset="32%" stopColor="rgba(253,111,111,0.80)" />
              <stop offset="63%" stopColor="rgba(255,198,113,0.80)" />
              <stop offset="88%" stopColor="rgba(255,255,255,0.80)" />
            </linearGradient>
          </defs>
        </svg>
        {hasPoint && (
          <span style={{ color: '#BC313D', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 700, lineHeight: '20px', textAlign: 'center' }}>
            +{item.point}
          </span>
        )}
      </div>

      <span style={{ flex: 1, color: '#6E625B', fontSize: 18, fontFamily: 'Noto Sans TC', fontWeight: 700, lineHeight: '27px' }}>
        {item.post_title}
      </span>

      <span style={{ color: 'rgba(110,98,91,0.50)', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '19.5px', flexShrink: 0 }}>
        {fmtDate(item.liked_at)}
      </span>
    </div>
  );
}

// ─── 實體互動 活動卡片 ────────────────────────────────────────────────────────
function PhysicalActivityCard({ item }) {
  const { t } = useLanguage();
  const eventDate = `${fmtDate(item.official_start_at)} ~ ${fmtDate(item.official_end_at)}`;
  const checkInDate = fmtDate(item.record_created_at);
  const pointDisplay = item.point != null ? `+${item.point.toLocaleString()}` : '';
  const pointColor = '#BC313D';

  return (
    <div
      className="w-full"
      style={{ background: 'white', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 8, outline: '1px rgba(110,98,91,0.08) solid', outlineOffset: '-1px' }}
    >
      <div
        className="flex items-baseline justify-between"
        style={{
          paddingTop: 2, paddingBottom: 6, paddingLeft: 16, paddingRight: 16,
          background: 'linear-gradient(134deg, rgba(250,242,219,0.80) 0%, white 18%, white 66%, rgba(249,241,218,0.60) 100%)',
          borderBottom: '1px solid rgba(197,155,90,0.08)',
        }}
      >
        <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{item.official_title}</span>
        <span style={{ color: pointColor, fontSize: 18, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '27px' }}>{pointDisplay}</span>
      </div>

      <div style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8 }}>
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <Chip color="red" label={t('profile.chipEventTime')} />
            <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{eventDate}</span>
          </div>
          <div className="flex items-start gap-2">
            <Chip color="red" label={t('profile.chipEventLocation')} />
            <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{item.monastery_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Chip color="gold" label={t('profile.chipCheckInTime')} />
            <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{checkInDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 積分互送 交換卡片 ────────────────────────────────────────────────────────
function ExchangeCard({ item }) {
  const { t } = useLanguage();
  const txDate = fmtDate(item.tx_time);
  const pointDisplay = item.point > 0 ? `+${item.point.toLocaleString()}` : `${item.point.toLocaleString()}`;

  return (
    <div
      className="w-full"
      style={{ background: 'white', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 8, outline: '1px rgba(110,98,91,0.08) solid', outlineOffset: '-1px' }}
    >
      <div
        className="flex items-baseline justify-between"
        style={{
          paddingTop: 2, paddingBottom: 6, paddingLeft: 16, paddingRight: 16,
          background: 'linear-gradient(134deg, rgba(250,242,219,0.80) 0%, white 18%, white 66%, rgba(249,241,218,0.60) 100%)',
          borderBottom: '1px solid rgba(197,155,90,0.08)',
        }}
      >
        <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{item.item_desc}</span>
        <span style={{ color: '#BC313D', fontSize: 18, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '27px' }}>{pointDisplay}</span>
      </div>

      <div style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8 }}>
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <Chip color="red" label={t('profile.monasteryLabel')} />
            <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{item.monastery_name}</span>
          </div>
          <div className="flex items-start gap-2">
            <Chip color="red" label={t('profile.chipDharmaOrName')} />
            <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{item.dharma_name !== "無" && item.dharma_name ? item.dharma_name : item.username}</span>
          </div>
          <div className="flex items-center gap-2">
            <Chip color="gold" label={t('profile.chipTime')} />
            <span style={{ color: '#6E625B', fontSize: 15, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '22.5px' }}>{txDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhysicalSection({ items, loading, totalPoints, onRuleClick }) {
  const { t } = useLanguage();
  return (
    <CollapsibleSection title={t('profile.sectionPhysical')} totalPoints={totalPoints} onRuleClick={onRuleClick}>
      <ScrollableList
        loading={loading}
        empty={t('profile.noCheckInRecords')}
        items={items}
        renderItem={(item) => <PhysicalActivityCard key={item.record_id} item={item} />}
      />
    </CollapsibleSection>
  );
}

function ExchangeSection({ items, loading, totalPoints, onRuleClick }) {
  const { t } = useLanguage();
  return (
    <CollapsibleSection title={t('profile.sectionPoints')} totalPoints={totalPoints} onRuleClick={onRuleClick}>
      <ScrollableList
        loading={loading}
        empty={t('profile.noGiftRecords')}
        items={items}
        renderItem={(item) => <ExchangeCard key={item.tx_id} item={item} />}
      />
    </CollapsibleSection>
  );
}

// ─── 活動積分 Tab ─────────────────────────────────────────────────────────────
export default function ActivityPointsTab({ boardItems, boardTotalPoints, boardLoading, activityItems, activityTotalPoints, activityLoading, lightingItems, lightingTotalPoints, lightingLoading, transmissionItems, transmissionTotalPoints, transmissionLoading, exchangeItems, exchangeTotalPoints, exchangeLoading, shadowItems, shadowTotalPoints, shadowLoading, onRuleClick, onBoardClick, onShadowClick }) {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage?.code === 'en';
  const jueGuangTitle = isEn ? t('jue-guang.pageTitle') : t('jue-guang.pageTitle').slice(0, 2);
  const jueGuangSubtitle = isEn ? null : t('jue-guang.pageTitle').slice(2);
  const zenHeartTitle = isEn ? t('profile.sectionZenHeart') : t('profile.sectionZenHeart').slice(0, 2);
  const zenHeartSubtitle = isEn ? null : t('profile.sectionZenHeart').slice(2);
  const wisdomLampTitle = isEn ? t('profile.sectionWisdomLamp') : t('profile.sectionWisdomLamp').slice(0, 2);
  const wisdomLampSubtitle = isEn ? null : t('profile.sectionWisdomLamp').slice(2);
  const transmissionTitle = isEn ? t('profile.sectionTransmission') : t('profile.sectionTransmission').slice(0, 2);
  const transmissionSubtitle = isEn ? null : t('profile.sectionTransmission').slice(2);

  return (
    <div className="flex flex-col w-full gap-4" style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 1, background: '#6E625B', pointerEvents: 'none' }} />

      {/* 庭影覺光 */}
      <CollapsibleSection title={jueGuangTitle} subtitle={jueGuangSubtitle} totalPoints={shadowTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <ScrollableList
          loading={shadowLoading}
          empty={t('profile.noConnectionRecords')}
          items={shadowItems}
          renderItem={(item, index) => <ShadowCard key={item.record_id || index} item={item} onClick={onShadowClick} />}
        />
      </CollapsibleSection>

      <TimelineDivider />

      {/* 禪心連線 */}
      <CollapsibleSection title={zenHeartTitle} subtitle={zenHeartSubtitle} totalPoints={boardTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <ScrollableList
          loading={boardLoading}
          empty={t('profile.noConnectionRecords')}
          items={boardItems}
          renderItem={(item, index) => <ZenBoardCard key={item.board_id || index} item={item} onClick={onBoardClick} />}
        />
      </CollapsibleSection>
      <TimelineDivider />

      {/* 文星點燈 */}
      <CollapsibleSection title={wisdomLampTitle} subtitle={wisdomLampSubtitle} totalPoints={lightingTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <ScrollableList
          loading={lightingLoading}
          empty={t('profile.noSupportRecords')}
          items={lightingItems}
          renderItem={(item) => <LightingHeartCard key={item.post_id} item={item} />}
        />
      </CollapsibleSection>

      <TimelineDivider />

      {/* 中台心傳 */}
      <CollapsibleSection title={transmissionTitle} subtitle={transmissionSubtitle} totalPoints={transmissionTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <ScrollableList
          loading={transmissionLoading}
          empty={t('profile.noTransmissionRecords')}
          items={transmissionItems}
          renderItem={(item) => <LightingHeartCard key={item.post_id} item={item} />}
        />
      </CollapsibleSection>

      <TimelineDivider />

      <PhysicalSection items={activityItems} loading={activityLoading} totalPoints={activityTotalPoints} onRuleClick={() => onRuleClick('points')} />

      <TimelineDivider />

      <ExchangeSection items={exchangeItems} loading={exchangeLoading} totalPoints={exchangeTotalPoints} onRuleClick={() => onRuleClick('points')} />

      <TimelineDivider />
    </div>
  );
}
